import { KatIcon } from "@amzn/katal-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ValidationSeverity } from "src/api/interfaces/nest";
import type { NestTestResultError } from "src/api/interfaces/nest/history";
import "./index.css";
import Markdown from "markdown-to-jsx";

type SortDirection = "asc" | "desc" | false;

interface NestHistoryErrorTableProps {
  data: NestTestResultError[];
}

export function NestHistoryErrorTable({ data }: NestHistoryErrorTableProps) {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [sortDirection, setSortDirection] = useState<SortDirection>(false);

  const toggleExpanded = (index: number) => {
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleSort = () => {
    setSortDirection((prev) =>
      prev === "asc" ? "desc" : prev === "desc" ? false : "asc"
    );
    setExpandedRows({});
  };

  const sortedData = sortDirection
    ? [...data].sort((a, b) => {
        const cmp = a.severity.localeCompare(b.severity);
        return sortDirection === "asc" ? cmp : -cmp;
      })
    : data;

  const canExpand = (row: NestTestResultError) =>
    !!(row.detailedMessage || row.solutionPath);

  const severityVariant = (s: string) =>
    s === ValidationSeverity.ERROR
      ? "error"
      : s === ValidationSeverity.WARNING
        ? "warning"
        : "info";

  return (
    <table className="nest-history-error-table">
      <thead>
        <tr>
          <th>{t("nest_history_error_line")}</th>
          <th>{t("nest_history_error_element")}</th>
          <th>{t("nest_history_error_error")}</th>
          <th>
            <button
              type="button"
              className="nest-error-sort-btn"
              onClick={toggleSort}
            >
              {t("nest_history_error_severity")}
              <KatIcon
                name={sortDirection ? "chevron-down" : "chevron-up"}
                size="small"
              />
            </button>
          </th>
          <th>{t("nest_history_error_message")}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {sortedData.map((row, index) => (
          <React.Fragment key={index}>
            <tr
              className="nest-error-row-clickable"
              onClick={() => canExpand(row) && toggleExpanded(index)}
            >
              <td>{row.line ?? "-"}</td>
              <td>{row.element}</td>
              <td>{t(row.error.key, row.error.parameters)}</td>
              <td>
                <span
                  className={`nest-error-severity-badge nest-error-severity-badge--${severityVariant(row.severity)}`}
                >
                  {row.severity}
                </span>
              </td>
              <td>{t(row.message.key, row.message.parameters)}</td>
              <td>
                {canExpand(row) && (
                  <KatIcon
                    name={
                      expandedRows[index] ? "chevron-down" : "chevron-right"
                    }
                    size="small"
                  />
                )}
              </td>
            </tr>
            {expandedRows[index] && (
              <tr className="nest-error-detail-row">
                <td colSpan={6}>
                  <div className="nest-error-detail-content">
                    {row.detailedMessage && (
                      <div className="nest-error-detail-item">
                        <h6>{t("nest_history_error_detailedMessage")}</h6>
                        <Markdown>
                          {t(
                            row.detailedMessage.key,
                            row.detailedMessage.parameters
                          )}
                        </Markdown>
                      </div>
                    )}
                    {row.solutionPath && (
                      <div className="nest-error-detail-item">
                        <h6>{t("nest_history_error_solutionPath")}</h6>
                        <Markdown>
                          {t(row.solutionPath.key, row.solutionPath.parameters)}
                        </Markdown>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}
